import { useEffect, useRef } from "react";
import { preload } from "react-dom";
import "@/components/prophets/prophets.css";
import { ProphetsHeader } from "@/components/prophets/ProphetsHeader";
import { ClickBurst } from "@/components/prophets/ClickBurst";
import { ProphetsHero } from "@/components/prophets/ProphetsHero";
import { ProphetsAbout } from "@/components/prophets/ProphetsAbout";
import { CustomCursor } from "@/components/prophets/CustomCursor";
import { MeetTheProphets } from "@/components/prophets/MeetTheProphets";
import { ProphetsInsight } from "@/components/prophets/ProphetsInsight";
import { ProphetsBenefits } from "@/components/prophets/ProphetsBenefits";
import { ProphetsGames } from "@/components/prophets/ProphetsGames";
import { ProphetsFinalCTA } from "@/components/prophets/ProphetsFinalCTA";
import { ProphetsFooter } from "@/components/prophets/ProphetsFooter";
// EXPERIMENT — remove these two lines (import + <TimelineInterference />
// below) to fully disable the "Timeline Interference" glitch overlay.
import TimelineInterference from "@/components/prophets/TimelineInterference";

// Kick off the fetch of the giant metallic P + PROPHETS wordmark as early
// as possible so it paints in the very first frame instead of after JS
// hydration. React 19 hoists this to <head> as <link rel="preload">, and
// the URL matches the <img src> below so the browser deduplicates the
// fetch (no double request). Scoped to `/` only — this module is only
// loaded on the landing route.
preload("/img/prophets_logo.webp", { as: "image", fetchPriority: "high" });

/**
 * Prophets landing page — mounted at route `/`.
 *
 * Fully isolated from the Family Race game (which lives at /family-race).
 * Only the hero + header are built for now; anchor placeholders for the
 * remaining sections are provided so the header nav scrolls smoothly to
 * their eventual locations.
 */
export default function ProphetsHome() {
  const rootRef = useRef(null);

  // Smooth-scroll for in-page hash anchors + reset scroll on mount.
  useEffect(() => {
    const html = document.documentElement;
    const prevScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "smooth";
    return () => { html.style.scrollBehavior = prevScrollBehavior; };
  }, []);

  return (
    <div ref={rootRef} className="prophets-root" data-testid="prophets-home">
      <CustomCursor rootRef={rootRef} />
      <ClickBurst />
      <ProphetsHeader />
      <main>
        <ProphetsHero />

        {/* Section 2 — Collection: Meet the Prophets carousel (`#collection`) */}
        <MeetTheProphets />

        {/* Section 3 — About / What is Prophets? (`#about`) */}
        <ProphetsAbout />

        {/* Section 4 — Holder Utilities (`#utilities`) */}
        <ProphetsBenefits />

        {/* Section 5 — Games (`#games`) */}
        <ProphetsGames />

        {/* Section 6 — Lore (3-panel Insight slider: `#lore`) */}
        <ProphetsInsight />

        {/* Section 7 — Final CTA */}
        <ProphetsFinalCTA />
      </main>

      <ProphetsFooter />

      {/* EXPERIMENT — Timeline Interference (see TimelineInterference.jsx) */}
      <TimelineInterference />
    </div>
  );
}
